import React from "react";
import * as XLSX from "xlsx";
import { useDropzone } from "react-dropzone";
import { Download, FileSpreadsheet, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { DataTable, DataTableColumn } from "../../components/ui/data-table";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { db } from "../../firebase";
import {
  extractEcountInventoryRows,
  extractEcountTotalCount,
  getEcountConfigFromEnv,
  getMissingEcountConfigFields,
  runEcountWarehouseInventoryFlow,
} from "../../lib/ecount";

type ParsedItem = {
  rowKey: string;
  warehouseCode: string;
  warehouseName: string;
  itemCode: string;
  itemName: string;
  spec: string;
  stockCount: number | null;
  stockText: string;
};

type HeaderMeta = {
  headerRowIndex: number;
  headers: string[];
  codeIndex: number;
  nameIndex: number;
  specIndex: number | null;
  stockIndex: number;
  stockCandidates: number[];
};

const REQUIRED_CODE_HEADERS = ["품목코드", "상품코드", "코드"];
const REQUIRED_NAME_HEADERS = ["품목명", "상품명", "품명"];
const OPTIONAL_SPEC_HEADERS = ["규격", "스펙"];
const STOCK_HINT_HEADERS = ["재고", "현황", "수량", "본사", "창고"];
const WAREHOUSE_INVENTORY_DOC_PATH = "warehouseInventory/current";

const formatBaseDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

const maskText = (value: string, opts: { head?: number; tail?: number } = {}) => {
  const { head = 4, tail = 4 } = opts;
  if (!value) return "-";
  if (value.length <= head + tail) return value;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
};

const normalizeHeader = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();

const toDisplayHeader = (value: unknown, index: number) => {
  const text = String(value ?? "").trim();
  return text || `컬럼${index + 1}`;
};

const toNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const normalized = String(value).replace(/,/g, "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const includesAny = (source: string, candidates: string[]) =>
  candidates.some((candidate) => source.includes(normalizeHeader(candidate)));

const sanitizeParsedItem = (value: any): ParsedItem => {
  const stockCount = toNumber(value?.stockCount);
  const fallbackStockText =
    stockCount == null ? String(value?.stockText ?? "").trim() : "";

  return {
    rowKey: String(value?.rowKey ?? "").trim(),
    warehouseCode: String(value?.warehouseCode ?? "").trim(),
    warehouseName: String(value?.warehouseName ?? "").trim(),
    itemCode: String(value?.itemCode ?? "").trim(),
    itemName: String(value?.itemName ?? "").trim(),
    spec: String(value?.spec ?? "").trim(),
    stockCount,
    stockText: fallbackStockText,
  };
};

const findHeaderMeta = (rows: unknown[][]): HeaderMeta | null => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] ?? [];
    const headers = row.map((cell, idx) => toDisplayHeader(cell, idx));
    const normalizedHeaders = headers.map((h) => normalizeHeader(h));

    const codeIndex = normalizedHeaders.findIndex((h) =>
      includesAny(h, REQUIRED_CODE_HEADERS)
    );
    const nameIndex = normalizedHeaders.findIndex((h) =>
      includesAny(h, REQUIRED_NAME_HEADERS)
    );

    if (codeIndex < 0 || nameIndex < 0) {
      continue;
    }

    const specIndex = normalizedHeaders.findIndex((h) =>
      includesAny(h, OPTIONAL_SPEC_HEADERS)
    );

    const excluded = new Set<number>([codeIndex, nameIndex]);
    if (specIndex >= 0) excluded.add(specIndex);

    const stockCandidates = normalizedHeaders
      .map((_, idx) => idx)
      .filter((idx) => !excluded.has(idx));

    if (stockCandidates.length === 0) {
      continue;
    }

    const hintedStockIndex = stockCandidates.find((idx) =>
      includesAny(normalizedHeaders[idx], STOCK_HINT_HEADERS)
    );
    const stockIndex = hintedStockIndex ?? stockCandidates[0];

    return {
      headerRowIndex: rowIndex,
      headers,
      codeIndex,
      nameIndex,
      specIndex: specIndex >= 0 ? specIndex : null,
      stockIndex,
      stockCandidates,
    };
  }

  return null;
};

const mapEcountRowsToParsedItems = (rows: any[]): ParsedItem[] =>
  rows
    .map((row, index) => {
      const stockCount = toNumber(
        row?.BAL_QTY ?? row?.QTY ?? row?.BALANCE_QTY ?? row?.QUANTITY
      );
      const warehouseCode = String(row?.WH_CD ?? "").trim();
      const warehouseName = String(row?.WH_DES ?? "").trim();
      const itemCode = String(row?.PROD_CD ?? row?.ITEM_CD ?? "").trim();
      const itemName = String(row?.PROD_DES ?? row?.ITEM_DES ?? "").trim();
      const spec = String(row?.PROD_SIZE_DES ?? row?.SIZE_DES ?? "").trim();

      return {
        rowKey: [warehouseCode, itemCode, itemName, spec, index].join("|"),
        warehouseCode,
        warehouseName,
        itemCode,
        itemName,
        spec,
        stockCount,
        stockText:
          stockCount == null
            ? String(
                row?.BAL_QTY ?? row?.QTY ?? row?.BALANCE_QTY ?? row?.QUANTITY ?? ""
              ).trim()
            : "",
      };
    })
    .filter((row) => row.itemCode || row.itemName);

const WarehouseInventory: React.FC = () => {
  const [items, setItems] = React.useState<ParsedItem[]>([]);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [selectedStockIndex, setSelectedStockIndex] = React.useState<number>(0);
  const [currentAffiliation, setCurrentAffiliation] = React.useState<string>("");
  const [loadingRemote, setLoadingRemote] = React.useState<boolean>(true);
  const [savingRemote, setSavingRemote] = React.useState<boolean>(false);
  const [persistedStockColumnTitle, setPersistedStockColumnTitle] =
    React.useState<string>("");
  const [stockColumnOptions, setStockColumnOptions] = React.useState<number[]>(
    []
  );
  const [baseColumns, setBaseColumns] = React.useState<{
    codeIndex: number;
    nameIndex: number;
    specIndex: number | null;
  } | null>(null);
  const [sourceRows, setSourceRows] = React.useState<unknown[][]>([]);
  const [fileName, setFileName] = React.useState<string>("");
  const [ecountBaseDate, setEcountBaseDate] = React.useState<string>(() =>
    formatBaseDate()
  );
  const [ecountWhCd, setEcountWhCd] = React.useState<string>("");
  const [ecountProdCd, setEcountProdCd] = React.useState<string>("");
  const [ecountLoading, setEcountLoading] = React.useState<boolean>(false);
  const [ecountStatus, setEcountStatus] = React.useState<string>("");
  const [ecountZone, setEcountZone] = React.useState<string>("");
  const [ecountSessionId, setEcountSessionId] = React.useState<string>("");
  const [ecountLastCount, setEcountLastCount] = React.useState<number | null>(
    null
  );
  const [ecountLastFetchedAt, setEcountLastFetchedAt] =
    React.useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const ecountConfig = getEcountConfigFromEnv();
  const missingEcountFields = getMissingEcountConfigFields(ecountConfig);
  const resetInventoryView = React.useCallback(() => {
    setItems([]);
    setHeaders([]);
    setStockColumnOptions([]);
    setBaseColumns(null);
    setSourceRows([]);
    setFileName("");
    setPersistedStockColumnTitle("");
    setSelectedStockIndex(0);
  }, []);

  const saveSnapshotToServer = React.useCallback(
    async (payload: {
      fileName: string;
      stockColumnTitle: string;
      items: ParsedItem[];
    }) => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        toast.error("로그인 정보가 없어 서버 저장을 할 수 없습니다.");
        return false;
      }

      try {
        setSavingRemote(true);
        await setDoc(doc(db, WAREHOUSE_INVENTORY_DOC_PATH), {
          scope: "hq",
          fileName: payload.fileName,
          stockColumnTitle: payload.stockColumnTitle,
          items: payload.items,
          recordCount: payload.items.length,
          uploadedBy: user.uid,
          updatedAt: serverTimestamp(),
        });
        return true;
      } catch (error) {
        console.error(error);
        toast.error("서버 저장에 실패했습니다.");
        return false;
      } finally {
        setSavingRemote(false);
      }
    },
    []
  );

  React.useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentAffiliation("");
        resetInventoryView();
        setLoadingRemote(false);
        return;
      }

      setLoadingRemote(true);
      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        const userData = userSnap.data() as any;
        const affiliation = String(userData?.affiliation ?? "").trim();
        setCurrentAffiliation(affiliation);
        if (!affiliation) setCurrentAffiliation("-");

        const snapshotRef = doc(db, WAREHOUSE_INVENTORY_DOC_PATH);
        const snapshotSnap = await getDoc(snapshotRef);
        if (!snapshotSnap.exists()) {
          resetInventoryView();
          return;
        }

        const data = snapshotSnap.data() as any;
        const serverItems: ParsedItem[] = Array.isArray(data.items)
          ? data.items
              .map((row: any) => sanitizeParsedItem(row))
              .filter((row: ParsedItem) => row.itemCode || row.itemName)
          : [];

        setHeaders([]);
        setStockColumnOptions([]);
        setSourceRows([]);
        setBaseColumns(null);
        setSelectedStockIndex(0);
        setFileName(String(data.fileName ?? ""));
        setPersistedStockColumnTitle(String(data.stockColumnTitle ?? ""));
        setItems(serverItems);
      } catch (error) {
        console.error(error);
        toast.error("서버 재고 데이터를 불러오지 못했습니다.");
      } finally {
        setLoadingRemote(false);
      }
    });

    return () => unsubscribe();
  }, [resetInventoryView]);

  const buildItems = React.useCallback(
    (
      rows: unknown[][],
      col: { codeIndex: number; nameIndex: number; specIndex: number | null },
      stockIndex: number
    ) => {
      const parsed = rows
        .map((row, index) => {
          const itemCode = String(row[col.codeIndex] ?? "").trim();
          const itemName = String(row[col.nameIndex] ?? "").trim();
          const specValue =
            col.specIndex != null ? String(row[col.specIndex] ?? "").trim() : "";
          const stockRaw = row[stockIndex];
          const stockCount = toNumber(stockRaw);
          return {
            rowKey: [itemCode, itemName, specValue, index].join("|"),
            warehouseCode: "",
            warehouseName: "",
            itemCode,
            itemName,
            spec: specValue,
            stockCount,
            stockText: stockCount == null ? String(stockRaw ?? "").trim() : "",
          };
        })
        .filter((row) => row.itemCode || row.itemName);

      setItems(parsed);
    },
    []
  );

  const parseExcel = React.useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const binary = event.target?.result;
          const workbook = XLSX.read(binary, { type: "binary" });
          if (!workbook.SheetNames.length) {
            toast.error("시트가 없는 엑셀 파일입니다.");
            resetInventoryView();
            return;
          }
          const firstSheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[firstSheetName];
          const matrix = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
          }) as unknown[][];

          const meta = findHeaderMeta(matrix);
          if (!meta) {
            toast.error(
              "헤더를 찾지 못했습니다. '품목코드', '품목명', 재고 컬럼이 포함된 파일을 업로드해 주세요."
            );
            resetInventoryView();
            return;
          }

          const rowsAfterHeader = matrix.slice(meta.headerRowIndex + 1);
          const availableStockOptions = meta.stockCandidates.filter((idx) =>
            rowsAfterHeader.some((row) => String(row[idx] ?? "").trim() !== "")
          );
          const stockOptions =
            availableStockOptions.length > 0
              ? availableStockOptions
              : meta.stockCandidates;
          const initialStockIndex = stockOptions.includes(meta.stockIndex)
            ? meta.stockIndex
            : stockOptions[0];
          const stockColumnTitle =
            meta.headers[initialStockIndex]?.trim() || "재고개수(현황)";
          const parsedItems = rowsAfterHeader
            .map((row, index) => {
              const itemCode = String(row[meta.codeIndex] ?? "").trim();
              const itemName = String(row[meta.nameIndex] ?? "").trim();
              const specValue =
                meta.specIndex != null
                  ? String(row[meta.specIndex] ?? "").trim()
                  : "";
              const stockRaw = row[initialStockIndex];
              const stockCount = toNumber(stockRaw);
              return {
                rowKey: [itemCode, itemName, specValue, index].join("|"),
                warehouseCode: "",
                warehouseName: "",
                itemCode,
                itemName,
                spec: specValue,
                stockCount,
                stockText:
                  stockCount == null ? String(stockRaw ?? "").trim() : "",
              };
            })
            .filter((row) => row.itemCode || row.itemName);

          setHeaders(meta.headers);
          setStockColumnOptions(stockOptions);
          setBaseColumns({
            codeIndex: meta.codeIndex,
            nameIndex: meta.nameIndex,
            specIndex: meta.specIndex,
          });
          setSelectedStockIndex(initialStockIndex);
          setSourceRows(rowsAfterHeader);
          setItems(parsedItems);
          setFileName(file.name);
          setPersistedStockColumnTitle(stockColumnTitle);

          const saved = await saveSnapshotToServer({
            fileName: file.name,
            stockColumnTitle,
            items: parsedItems,
          });

          if (saved) {
            toast.success(`${file.name} 업로드 및 서버 저장 완료`);
          } else {
            toast.warning("파일은 표시되지만 서버 저장은 실패했습니다.");
          }
        } catch (error) {
          console.error(error);
          toast.error("엑셀 파일을 읽는 중 오류가 발생했습니다.");
        }
      };
      reader.readAsBinaryString(file);
    },
    [resetInventoryView, saveSnapshotToServer]
  );

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) parseExcel(file);
    },
    [parseExcel]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
  });

  const handleStockColumnChange = (value: string) => {
    const nextIndex = Number(value);
    if (!Number.isFinite(nextIndex) || !baseColumns) {
      return;
    }
    setSelectedStockIndex(nextIndex);
    setPersistedStockColumnTitle(
      headers[nextIndex]?.trim() || "재고개수(현황)"
    );
    buildItems(sourceRows, baseColumns, nextIndex);
  };

  const stockColumnTitle = headers[selectedStockIndex]?.trim()
    ? headers[selectedStockIndex]?.trim()
    : persistedStockColumnTitle || "재고개수(현황)";

  const handleSaveCurrentView = async () => {
    if (items.length === 0) {
      toast.warning("저장할 재고 데이터가 없습니다.");
      return;
    }
    const saved = await saveSnapshotToServer({
      fileName: fileName || "재고현황",
      stockColumnTitle,
      items,
    });
    if (saved) {
      toast.success("현재 재고 데이터를 서버에 저장했습니다.");
    }
  };

  const handleFetchEcountInventory = async () => {
    if (missingEcountFields.length > 0) {
      toast.error("이카운트 환경변수가 비어 있습니다.");
      setEcountStatus(`설정 누락: ${missingEcountFields.join(", ")}`);
      return;
    }

    if (!/^\d{8}$/.test(ecountBaseDate)) {
      toast.error("조회일자는 YYYYMMDD 형식으로 입력해 주세요.");
      setEcountStatus("조회일자 형식 오류");
      return;
    }

    try {
      setEcountLoading(true);
      setEcountStatus("ZONE 조회 및 로그인 중...");

      const result = await runEcountWarehouseInventoryFlow({
        config: ecountConfig,
        request: {
          baseDate: ecountBaseDate,
          whCd: ecountWhCd.trim(),
          prodCd: ecountProdCd.trim(),
        },
      });

      console.groupCollapsed(
        `[ECOUNT] 창고재고 응답 ${ecountBaseDate} (${new Date().toLocaleString(
          "ko-KR"
        )})`
      );
      console.log("zoneResponse", result.zoneResponse);
      console.log("loginResponse", result.loginResponse);
      console.log("inventoryResponse", result.inventoryResponse);
      console.groupEnd();

      const rows = extractEcountInventoryRows(result.inventoryResponse);
      const totalCount = extractEcountTotalCount(result.inventoryResponse);
      const nextItems = mapEcountRowsToParsedItems(rows);

      setEcountZone(result.zone);
      setEcountSessionId(result.sessionId);
      setEcountLastCount(totalCount ?? nextItems.length);
      setEcountLastFetchedAt(new Date().toLocaleString("ko-KR"));
      setEcountStatus(
        `조회 완료${(totalCount ?? nextItems.length) >= 0 ? ` (${totalCount ?? nextItems.length}건)` : ""}`
      );

      if (nextItems.length > 0) {
        setHeaders([]);
        setStockColumnOptions([]);
        setBaseColumns(null);
        setSourceRows([]);
        setSelectedStockIndex(0);
        setPersistedStockColumnTitle("BAL_QTY");
        setFileName(`ECOUNT API ${ecountBaseDate}`);
        setItems(nextItems);
      }

      toast.success("이카운트 창고재고 조회가 완료되었습니다. Console을 확인하세요.");
    } catch (error) {
      console.error("[ECOUNT] 창고재고 조회 실패", error);
      const message =
        error instanceof Error
          ? error.message
          : "이카운트 창고재고 조회에 실패했습니다.";
      setEcountStatus(message);
      toast.error(message);
    } finally {
      setEcountLoading(false);
    }
  };

  const columns: DataTableColumn<ParsedItem>[] = [
    {
      title: "번호",
      width: 70,
      align: "center",
      render: (_: unknown, __: ParsedItem, index: number) => index + 1,
    },
    {
      title: "창고코드",
      dataIndex: "warehouseCode",
      sortable: true,
      sortKey: "warehouseCode",
      width: 140,
    },
    {
      title: "창고명",
      dataIndex: "warehouseName",
      sortable: true,
      sortKey: "warehouseName",
      width: 220,
    },
    {
      title: "품목코드",
      dataIndex: "itemCode",
      sortable: true,
      sortKey: "itemCode",
      width: 240,
    },
    {
      title: "품목명",
      dataIndex: "itemName",
      sortable: true,
      sortKey: "itemName",
    },
    {
      title: "규격",
      dataIndex: "spec",
      sortable: true,
      sortKey: "spec",
      width: 180,
    },
    {
      title: `${stockColumnTitle} (재고개수)`,
      key: "stockCount",
      align: "right",
      sortable: true,
      sortFn: (a, b) => (a.stockCount ?? -Infinity) - (b.stockCount ?? -Infinity),
      render: (_: unknown, row: ParsedItem) =>
        row.stockCount != null ? row.stockCount.toLocaleString() : row.stockText || "-",
      width: 180,
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-extrabold">창고 재고</h1>
        <p className="text-sm text-muted-foreground">
          이카운트 API 또는 엑셀 업로드로 품목별 재고 현황을 확인할 수 있습니다.
        </p>
        <p className="text-xs text-muted-foreground">
          로그인 소속: {currentAffiliation || "-"} | 저장 위치: 본사 창고 공용
        </p>
      </div>

      <Card className="space-y-4 p-4">
        <div>
          <h2 className="text-base font-bold">이카운트 API</h2>
          <p className="text-xs text-muted-foreground">
            `Zone 조회 → 로그인 → 창고재고 조회` 순서로 실행합니다. 현재 설정된
            호스트 기준으로 최종 응답을 브라우저 console에 출력합니다.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">COM_CODE</div>
            <div className="mt-1 text-sm font-medium">
              {ecountConfig.comCode || "-"}
            </div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">USER_ID</div>
            <div className="mt-1 text-sm font-medium">
              {ecountConfig.userId || "-"}
            </div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">API_CERT_KEY</div>
            <div className="mt-1 text-sm font-medium">
              {maskText(ecountConfig.apiCertKey, { head: 6, tail: 4 })}
            </div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">LAN_TYPE</div>
            <div className="mt-1 text-sm font-medium">
              {ecountConfig.lanType || "-"}
            </div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">HOST</div>
            <div className="mt-1 text-sm font-medium">
              {ecountConfig.apiHostPrefix || "-"}
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">BASE_DATE</label>
            <Input
              value={ecountBaseDate}
              onChange={(event) => setEcountBaseDate(event.target.value)}
              placeholder="YYYYMMDD"
              maxLength={8}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              WH_CD (선택)
            </label>
            <Input
              value={ecountWhCd}
              onChange={(event) => setEcountWhCd(event.target.value)}
              placeholder="창고코드"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              PROD_CD (선택)
            </label>
            <Input
              value={ecountProdCd}
              onChange={(event) => setEcountProdCd(event.target.value)}
              placeholder="품목코드"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={handleFetchEcountInventory}
            disabled={ecountLoading || loadingRemote || missingEcountFields.length > 0}
          >
            <Download className="mr-2 h-4 w-4" />
            {ecountLoading ? "이카운트 조회 중..." : "창고재고 가져오기"}
          </Button>
          <div className="text-sm text-muted-foreground">
            {ecountStatus || "대기 중"}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-md bg-muted/40 p-3 text-xs">
            <div className="text-muted-foreground">마지막 ZONE</div>
            <div className="mt-1 font-medium text-foreground">
              {ecountZone || "-"}
            </div>
          </div>
          <div className="rounded-md bg-muted/40 p-3 text-xs">
            <div className="text-muted-foreground">SESSION_ID</div>
            <div className="mt-1 font-medium text-foreground">
              {maskText(ecountSessionId, { head: 8, tail: 6 })}
            </div>
          </div>
          <div className="rounded-md bg-muted/40 p-3 text-xs">
            <div className="text-muted-foreground">조회건수</div>
            <div className="mt-1 font-medium text-foreground">
              {ecountLastCount != null ? `${ecountLastCount}건` : "-"}
            </div>
          </div>
          <div className="rounded-md bg-muted/40 p-3 text-xs">
            <div className="text-muted-foreground">마지막 조회시각</div>
            <div className="mt-1 font-medium text-foreground">
              {ecountLastFetchedAt || "-"}
            </div>
          </div>
        </div>

        {missingEcountFields.length > 0 && (
          <div className="text-xs text-red-600">
            `.env.local` 설정이 누락되었습니다: {missingEcountFields.join(", ")}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div
            {...getRootProps()}
            className={`flex min-h-[72px] flex-1 cursor-pointer items-center gap-3 rounded-md border border-dashed px-4 py-3 text-sm ${
              isDragActive ? "bg-muted" : "hover:bg-muted/60"
            }`}
          >
            <input {...getInputProps()} />
            <UploadCloud className="h-5 w-5 text-muted-foreground" />
            <div className="space-y-0.5">
              <div className="font-medium">엑셀 파일 업로드</div>
              <div className="text-xs text-muted-foreground">
                파일을 드래그하거나 클릭해서 선택하세요. (.xlsx, .xls)
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loadingRemote}
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            파일 선택
          </Button>
          <Button
            type="button"
            onClick={handleSaveCurrentView}
            disabled={loadingRemote || savingRemote || items.length === 0}
          >
            {savingRemote ? "서버 저장 중..." : "현재 데이터 서버 저장"}
          </Button>
        </div>

        {loadingRemote && (
          <div className="mt-3 text-xs text-muted-foreground">
            서버에서 저장된 재고 데이터를 확인하는 중입니다...
          </div>
        )}

        {fileName && (
          <div className="mt-3 text-sm text-muted-foreground">
            업로드 파일: <b className="text-foreground">{fileName}</b>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) parseExcel(file);
            event.target.value = "";
          }}
        />
      </Card>

      <DataTable
        columns={columns}
        dataSource={items}
        rowKey="rowKey"
        pagination={{ pageSize: 20 }}
        search={{
          keys: ["warehouseCode", "warehouseName", "itemCode", "itemName", "spec"],
          placeholder: "창고/품목코드/품목명/규격 검색",
        }}
        emptyMessage="창고재고 가져오기 또는 엑셀 업로드 후 재고 현황이 표시됩니다."
        toolbarRight={
          headers.length > 0 && stockColumnOptions.length > 0 ? (
            <Select
              value={String(selectedStockIndex)}
              onValueChange={handleStockColumnChange}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="재고 컬럼 선택" />
              </SelectTrigger>
              <SelectContent>
                {stockColumnOptions.map((index) => (
                  <SelectItem
                    key={`${headers[index] ?? "컬럼"}-${index}`}
                    value={String(index)}
                  >
                    {headers[index] ?? `컬럼${index + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        }
      />
    </div>
  );
};

export default WarehouseInventory;
